import { Theme } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import { Leg, SearchCandidate } from 'gen/stream-api/requests/types.gen';
import { PositionShares } from 'types/optionsCalculator';
import { CONTRACT_MULTIPLIER } from 'config/riskEngine';

export const formatExpiry = (iso: string | undefined): string => {
  if (iso == null || iso.length === 0) {
    return '';
  }
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
};

export type RiskTier = 'low' | 'medium' | 'high';

export const TIER_LABEL: Record<RiskTier, string> = {
  low: 'LOW RISK SCORE',
  medium: 'MEDIUM RISK SCORE',
  high: 'HIGH RISK SCORE',
};

export const LOW_RISK_MAX = 30;
export const MEDIUM_RISK_MAX = 60;

export const tierFromRiskScore = (total: number): RiskTier =>
  total < LOW_RISK_MAX ? 'low' : total < MEDIUM_RISK_MAX ? 'medium' : 'high';

export const tierColor = (tier: RiskTier | null, theme: Theme): string => {
  if (tier === 'low') {
    return theme.palette.success.main;
  }
  if (tier === 'medium') {
    return theme.palette.warning.main;
  }
  if (tier === 'high') {
    return theme.palette.error.main;
  }
  return theme.palette.sgGreen;
};

export function usd(v: number | null | undefined): string {
  if (v == null) {
    return 'N/A';
  }
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const UNBOUNDED_LABEL = '∞';
const UNAVAILABLE_LABEL = '—';

type CandidateMoney = Pick<
  SearchCandidate,
  | 'max_profit_dollars'
  | 'max_risk_dollars'
  | 'max_profit_unbounded'
  | 'max_risk_unbounded'
  | 'net_credit'
  | 'net_debit'
>;

// null = missing; Infinity = unbounded.
const maxProfitDollars = (c: CandidateMoney): number | null =>
  c.max_profit_unbounded ? Infinity : (c.max_profit_dollars ?? null);

const maxRiskDollars = (c: CandidateMoney): number | null =>
  c.max_risk_unbounded ? Infinity : (c.max_risk_dollars ?? null);

const formatBound = (dollars: number | null): string => {
  if (dollars == null) {
    return UNAVAILABLE_LABEL;
  }
  return dollars === Infinity ? UNBOUNDED_LABEL : usd(dollars);
};

export const formatMaxProfit = (c: CandidateMoney): string =>
  formatBound(maxProfitDollars(c));

export const formatMaxRisk = (c: CandidateMoney): string =>
  formatBound(maxRiskDollars(c));

export const rewardToRiskRatio = (c: CandidateMoney): number | null => {
  const profit = maxProfitDollars(c);
  const risk = maxRiskDollars(c);
  if (
    profit == null ||
    risk == null ||
    !Number.isFinite(profit) ||
    !Number.isFinite(risk) ||
    risk <= 0
  ) {
    return null;
  }
  return profit / risk;
};

export const netCash = (
  c: CandidateMoney,
): { label: 'Net Debit' | 'Net Credit'; value: string } => {
  const isDebit = c.net_debit > 0;
  const perShare = isDebit ? c.net_debit : c.net_credit;
  return {
    label: isDebit ? 'Net Debit' : 'Net Credit',
    value: usd(perShare * CONTRACT_MULTIPLIER),
  };
};

// Stock legs are `quantity` lots of `multiplier` shares; the calculator wants shares + per-share price.
export const stockLegToShares = (leg: Leg, sym: string): PositionShares => {
  const lots = leg.quantity ?? 1;
  const shares = lots * (leg.multiplier ?? CONTRACT_MULTIPLIER);
  return {
    sym,
    quantity: leg.action === 'Sell' ? -shares : shares,
    price_paid: lots !== 0 ? leg.price / lots : leg.price,
  };
};

export function pct0(v: number | null | undefined): string {
  if (v == null) {
    return 'N/A';
  }
  return `${Math.round(v * 100)}%`;
}

export const pct = (v: number | null | undefined) =>
  v != null ? `${(v * 100).toFixed(1)}%` : 'N/A';
export const num = (v: number | null | undefined, d = 2) =>
  v != null ? v.toFixed(d) : 'N/A';

export const getMLConfidenceColor = (score: number, theme: Theme): string => {
  if (score >= 0.25) {
    return theme.palette.success.dark;
  }
  if (score >= 0.15) {
    return theme.palette.success.main;
  }
  if (score >= 0.1) {
    return theme.palette.warning.dark;
  }
  if (score >= 0.05) {
    return theme.palette.warning.main;
  }
  return theme.palette.text.secondary;
};

export const getVRPColor = (vrp: number, theme: Theme): string => {
  if (vrp > 0) {
    return theme.palette.success.main;
  }
  if (vrp < 0) {
    return theme.palette.error.main;
  }
  return theme.palette.text.secondary;
};

export const getCarryColor = (quality: string, theme: Theme): string => {
  if (quality === 'HIGH') {
    return theme.palette.success.main;
  }
  if (quality === 'LOW') {
    return theme.palette.error.main;
  }
  return theme.palette.warning.main;
};

export const getEMA = (values: number[], windowSize: number = 17): number[] => {
  const k = 2 / (windowSize + 1);
  const ema: number[] = [];
  ema[0] = values[0];
  for (let i = 1; i < values.length; i++) {
    ema[i] = values[i] * k + ema[i - 1] * (1 - k);
  }
  return ema;
};

// Rust's TVTP regime strings ('LowVol', ...) mapped to the keys getTVTPColor expects.
export const TVTP_REGIME_LABELS: Record<string, string> = {
  LowVol: 'LOW_VOL',
  Normal: 'NORMAL',
  HighVol: 'HIGH_VOL',
  Crisis: 'CRISIS',
};

export const getTVTPColor = (regime: string, theme: Theme): string => {
  switch (regime) {
    case 'LOW_VOL':
      return theme.palette.success.main;
    case 'NORMAL':
      return theme.palette.info.main;
    case 'HIGH_VOL':
      return theme.palette.warning.main;
    case 'CRISIS':
      return theme.palette.error.main;
    default:
      return theme.palette.text.primary;
  }
};
